import FormInput from '../../../components/FormInput';
import FormEntry from '../../../components/FormEntry';
import FormCard from '../../../components/FormCard';
import RadioOption from '../../../components/RadioOption';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import SectionHeader from 'components/SectionHeader';
import { SessionAnalysisType, SessionCatalog, SessionStatus } from '../../../types/SessionAnalysisType';
import ParentSessionSelector from '../../../components/ParentSessionSelector';
import VelodromeRouteSelector from '../../../components/VelodromeRouteSelector';
import { useProfile } from '../../../hooks/useProfile';

type TestType = 'rider' | 'equipment';

export default function NewSessionParameters(props: {
  onTestTypeWarningChange?: (show: boolean) => void;
  onVelodromeWarningChange?: (show: boolean) => void;
}) {
  const { onTestTypeWarningChange, onVelodromeWarningChange } = props;
  const { watch, setValue, register } = useFormContext();
  const { data: { show_blob_controls } } = useProfile();
  const [showTestTypeWarning, setShowTestTypeWarning] = useState<boolean>(false);

  // Watch for analysis_type changes to handle forced equipment test modes
  const analysisType = watch('analysis_type');
  // Session-level data source — two independent settings: where runs come
  // from on upload, and what a reprocess rebuilds from. Both default false
  // (file), and both are read off the form value so they survive the
  // route-picker stash.
  // The stash round-trip can bring a value back as the string 'true'.
  const watchBool = (field: string): boolean => {
    const value = watch(field);
    return value === true || value === 'true';
  };
  const chunkUpload = watchBool('chunk_upload');
  const reprocessFromBlob = watchBool('reprocess_from_blob');
  // Velodrome sessions store the chosen velodrome as the session route.
  const routeId = watch('route_id');
  const isEquipmentTestForced =
    Number(analysisType) === SessionAnalysisType.FREERIDE_UNSTRUCTURED ||
    Number(analysisType) === SessionAnalysisType.TRAVERSE_TYRE_REFERENCE ||
    Number(analysisType) === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON;

  // Check if TRAVERSE_TYRE_COMPARISON is selected
  // Convert to number in case it's stored as a string in the form
  const showParentSessionDropdown = Number(analysisType) === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON;

  // Check if velodrome type is selected
  const isVelodromeIndoors = Number(analysisType) === SessionAnalysisType.LOOP_VELODROME_INDOORS;
  const isVelodromeOutdoors = Number(analysisType) === SessionAnalysisType.LOOP_VELODROME_OUTDOORS;
  const isVelodrome = isVelodromeIndoors || isVelodromeOutdoors;

  // Check if test type is selected and show warning if not
  useEffect(() => {
    const subscription = watch((value) => {
      const analysisType = value.analysis_type;
      const shouldShowWarning = !analysisType;
      setShowTestTypeWarning(shouldShowWarning);
      if (onTestTypeWarningChange) {
        onTestTypeWarningChange(shouldShowWarning);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, onTestTypeWarningChange]);

  // Check test type selection on mount
  useEffect(() => {
    const shouldShowWarning = !watch('analysis_type');
    setShowTestTypeWarning(shouldShowWarning);
    if (onTestTypeWarningChange) {
      onTestTypeWarningChange(shouldShowWarning);
    }
  }, [watch, onTestTypeWarningChange]);

  // A velodrome session must have a velodrome (route) selected before it can be
  // created — mirror the mass/test-type "required" gating by surfacing a warning.
  useEffect(() => {
    if (onVelodromeWarningChange) {
      onVelodromeWarningChange(isVelodrome && !routeId);
    }
  }, [isVelodrome, routeId, onVelodromeWarningChange]);

  // Rider/equipment now lives entirely in config A's head/chest slope origins: a rider
  // test pins them manual-0 (so the drag-model solve holds body position at 0), equipment
  // leaves them automatic. (The legacy calculation_version column is no longer written.)
  const applyTestType = (type: TestType) => {
    const slope = type === 'rider' ? 0 : null;
    setValue('head_slope', slope, { shouldValidate: true });
    setValue('chest_slope', slope, { shouldValidate: true });
  };

  // The radio is derived from that encoding rather than mirrored in state, so
  // it survives a form restore (route-picker round trip) for free. Rider means
  // BOTH slopes are pinned; either one null (auto) reads as equipment.
  const testType: TestType =
    isEquipmentTestForced || watch('head_slope') === null || watch('chest_slope') === null
      ? 'equipment'
      : 'rider';

  // Normalise the slopes on mount (a fresh form has them undefined) and when
  // an analysis-type change forces equipment mode.
  useEffect(() => {
    applyTestType(isEquipmentTestForced ? 'equipment' : testType);
  }, [isEquipmentTestForced]);

  // Handle test type change
  const handleTestTypeChange = (newTestType: TestType) => {
    if (isEquipmentTestForced) return; // Don't allow changes if forced

    applyTestType(newTestType);
  };

  return (
    <div className="cr-mb-4 cr-flex cr-w-full cr-flex-[1_1] cr-flex-col">
      <div className="cr-h-full cr-w-full">
        <div className="cr-flex cr-flex-col">
          <SectionHeader title="Initial Configuration" />

          {/* Session Test Type */}
          <FormCard
            className={showTestTypeWarning ? 'cr-animate-pulse cr-ring-2 cr-ring-orange-400 cr-ring-offset-2' : ''}
          >
            <FormEntry label="Session Test Type">
              <FormInput
                fieldName="analysis_type"
                required="Session Test Type is Required"
                options={Object.entries(SessionCatalog)
                  .map(([analysisType, [name, sortOrder, status]]) => ({
                    id: Number(analysisType),
                    name: name as string,
                    sortOrder: sortOrder as number,
                    status: status as SessionStatus,
                    disabled: status === SessionStatus.COMING_SOON || status === SessionStatus.IN_DEVELOPMENT,
                  }))
                  .sort((a, b) => a.sortOrder - b.sortOrder)}
                optionLabelFn={(option) => {
                  let label = option.name;
                  if (option.status === SessionStatus.BETA) {
                    label += ' - Beta Phase';
                  } else if (option.status === SessionStatus.COMING_SOON) {
                    label += ' - Coming Soon';
                  } else if (option.status === SessionStatus.IN_DEVELOPMENT) {
                    label += ' - Coming Soon';
                  }
                  return label;
                }}
                emptyOption="Select Session Test Type"
                customClass="!cr-w-full"
              />
            </FormEntry>
          </FormCard>

          {/* Parent Session Selection for TRAVERSE_TYRE_COMPARISON */}
          <ParentSessionSelector showDropdown={showParentSessionDropdown} currentSessionId={undefined} />

          {/* Velodrome Routes Selector */}
          {isVelodrome && (
            <>
              <input type="hidden" {...register('route_id')} />
              <VelodromeRouteSelector
                analysisType={Number(analysisType)}
                selectedRouteId={routeId || undefined}
                onRouteSelect={(id) => setValue('route_id', id ?? '', { shouldValidate: true })}
                className={`cr-mb-4 ${
                  !routeId ? 'cr-animate-pulse cr-rounded-lg cr-ring-2 cr-ring-orange-400 cr-ring-offset-2' : ''
                }`}
              />
            </>
          )}

          {/* Test Type Selection */}
          <FormCard>
            <FormEntry label="Test Type">
              <div className="cr-flex cr-flex-col cr-gap-2">
                <label
                  className={`cr-flex cr-items-center ${isEquipmentTestForced ? 'cr-cursor-not-allowed cr-opacity-50' : 'cr-cursor-pointer'}`}
                >
                  <input
                    type="radio"
                    name="testType"
                    value="rider"
                    checked={testType === 'rider'}
                    onChange={() => handleTestTypeChange('rider')}
                    disabled={isEquipmentTestForced}
                    className="cr-mr-2"
                  />
                  <span>Rider Test</span>
                </label>
                <label
                  className={`cr-flex cr-items-center ${isEquipmentTestForced ? 'cr-cursor-not-allowed cr-opacity-50' : 'cr-cursor-pointer'}`}
                >
                  <input
                    type="radio"
                    name="testType"
                    value="equipment"
                    checked={testType === 'equipment'}
                    onChange={() => handleTestTypeChange('equipment')}
                    disabled={isEquipmentTestForced}
                    className="cr-mr-2"
                  />
                  <span>Equipment Test <span className="cr-italic cr-text-gray-500">(Body Position Compensated)</span></span>
                  {isEquipmentTestForced && (
                    <span className="cr-ml-2 cr-text-sm cr-text-gray-500">(Required for this session type)</span>
                  )}
                </label>
              </div>
              {/* Config A body-slope origins: rider = manual-0, equipment = unset (auto) */}
              <input type="hidden" {...register('head_slope')} />
              <input type="hidden" {...register('chest_slope')} />
            </FormEntry>
          </FormCard>

          {/* Data Source Selection (rides the RHF form + route-picker stash).
              Internal diagnostic surface — hidden for non-admins, whose
              sessions take the server defaults (file upload / stored file). */}
          {show_blob_controls && (
          <FormCard>
            <FormEntry label="Upload Source">
              <div className="cr-flex cr-flex-col cr-gap-2">
                <RadioOption
                  name="uploadSource"
                  value="file"
                  checked={!chunkUpload}
                  onChange={() => setValue('chunk_upload', false, { shouldValidate: true })}
                  label="File Upload"
                  hint="(default)"
                />
                <RadioOption
                  name="uploadSource"
                  value="chunks"
                  checked={chunkUpload}
                  onChange={() => setValue('chunk_upload', true, { shouldValidate: true })}
                  label="Chunk Upload"
                  hint="(live blob stream is the run data source)"
                />
              </div>
              <input type="hidden" {...register('chunk_upload')} />
            </FormEntry>
            <FormEntry label="Reprocess Source">
              <div className="cr-flex cr-flex-col cr-gap-2">
                <RadioOption
                  name="reprocessSource"
                  value="file"
                  checked={!reprocessFromBlob}
                  onChange={() => setValue('reprocess_from_blob', false, { shouldValidate: true })}
                  label="Stored File"
                  hint="(default)"
                />
                <RadioOption
                  name="reprocessSource"
                  value="blob"
                  checked={reprocessFromBlob}
                  onChange={() => setValue('reprocess_from_blob', true, { shouldValidate: true })}
                  label="Rebuild from Blob"
                  hint="(re-extract from device blobs each reprocess)"
                />
              </div>
              <input type="hidden" {...register('reprocess_from_blob')} />
            </FormEntry>
          </FormCard>
          )}
        </div>
      </div>
    </div>
  );
}
